import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { updateDisplayOnServer } from "../../../../utils/indexedDB";
import { useAtom } from "jotai";
import { AUTODISPLAY_DISPLAY_LIST } from "../../../AtomStates";
import PopupWrap from "../../../../assets/styles/Popup";
import images from "../../../../assets/styles/Images";
import { BoxWrap } from "../../../../assets/styles/BusinessAnalysisStyle";
import { Button } from "../../../../assets/styles/ButtonStyle";
import {
  Body1,
  Body2,
  Caption1,
  Body3,
} from "../../../../assets/styles/Typography";

const AutodisplayApiSelectedPopup = ({
  onClose,
  onSelect,
  onSaveNewDisplay,
  selectedEditDisplayId,
  handleRigistDisplay = false,
}) => {
  // 아톰에서 디스플레이 목록 가져오기 (읽기/쓰기 모두)
  const [displayList, setDisplayList] = useAtom(AUTODISPLAY_DISPLAY_LIST);
  const [showAddSize, setShowAddSize] = useState(false);
  const [selectedDisplay, setSelectedDisplay] = useState(null);
  const [showResolutionDropdown, setShowResolutionDropdown] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState("");
  const [isCustomResolution, setIsCustomResolution] = useState(false);
  const [customResolutionInput, setCustomResolutionInput] = useState("");
  const customInputRef = useRef(null);
  const [showInchDropdown, setShowInchDropdown] = useState(false);
  const [selectedInch, setSelectedInch] = useState("");
  const [isCustomInch, setIsCustomInch] = useState(false);
  const [customInchInput, setCustomInchInput] = useState("");
  const customInchInputRef = useRef(null);
  const [displayName, setDisplayName] = useState("");
  const [calculatedSize, setCalculatedSize] = useState({
    width: "",
    height: "",
  });
  const [editingDisplay, setEditingDisplay] = useState(null);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [displayOptions, setDisplayOptions] = useState([]);

  useEffect(() => {
    const options = displayList.map((device) => ({
      id: device._id,
      title: device.deviceTitle || "디스플레이 기기명",
      specs: `${device.deviceSpec.deviceInch || "정보없음"} / ${
        device.deviceSpec.deviceDPI || "정보없음"
      } / ${
        device.deviceSpec
          ? `${device.deviceSpec.deviceWidthMM}×${device.deviceSpec.deviceHeightMM}mm`
          : "사이즈 정보 없음"
      }`,
      originalData: device,
    }));
    setDisplayOptions(options);
  }, [displayList]);

  const resolutionOptions = [
    { id: "hd", label: "(가로)HD | 1280 x 720" },
    { id: "fhd", label: "(가로)FHD | 1920 x 1080" },
    { id: "qhd", label: "(가로)QHD (2K) | 2560 x 1440" },
    { id: "uhd", label: "(가로)UHD (4K) | 3840 x 2160" },
    { id: "hd", label: "(세로)HD | 720   x 1280" },
    { id: "fhd", label: "(세로)FHD | 1080 x 1920" },
    { id: "qhd", label: "(세로)QHD (2K) | 1440 x 2560" },
    { id: "uhd", label: "(세로)UHD (4K) | 2160 x 3840" },
    { id: "custom", label: "기타 (입력해주세요)" },
  ];

  const inchOptions = [
    { id: "21.5", label: "21.5 인치" },
    { id: "27", label: "27 인치" },
    { id: "32", label: "32 인치" },
    { id: "43", label: "43 인치" },
    { id: "55", label: "55 인치" },
    { id: "65", label: "65 인치" },
    { id: "custom", label: "기타 (입력해주세요)" },
  ];

  const handleDisplaySelect = (displayId) => {
    setSelectedDisplay(displayId);
  };

  const handleComplete = () => {
    if (selectedDisplay && onSelect) {
      onSelect(selectedDisplay);
    }
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (handleRigistDisplay) {
      setShowAddSize(true);
    }
  }, [handleRigistDisplay]);

  const handleAddSizeClick = () => {
    setShowAddSize(true);
  };

  const handleBackToSelection = () => {
    setShowAddSize(false);
    setEditingDisplay(null);
    setDisplayName("");
    setSelectedResolution("");
    setSelectedInch("");
    setIsCustomResolution(false);
    setIsCustomInch(false);
    setCustomResolutionInput("");
    setCustomInchInput("");
    setCalculatedSize({ width: "", height: "" });
  };

  const handleEditDisplay = (display) => {
    setEditingDisplay(display);

    // 기존 정보로 폼 채우기
    setDisplayName(display.title);

    // specs에서 정보 파싱
    const specs = display.specs.split(" / ");
    if (specs.length >= 3) {
      const inch = specs[0];
      const resolution = specs[1];

      setSelectedInch(inch);
      setSelectedResolution(resolution);

      // 사이즈 계산
      const size = calculateSizeFromInch(inch, resolution);
      setCalculatedSize(size);
    }

    setShowAddSize(true);
  };

  const handleEditDisplayFromDeviceSpec = (display) => {
    setEditingDisplay(display);

    setDisplayName(display.deviceTitle);

    // deviceSpec에서 정보 파싱
    const spec = display.deviceSpec || {};
    const inch = spec.deviceInch || "";
    const resolution = spec.deviceDPI || "";

    setSelectedInch(inch);
    setSelectedResolution(resolution);

    // 사이즈 계산 (기존 calculateSizeFromInch 함수 활용)
    if (inch && resolution) {
      const size = calculateSizeFromInch(inch, resolution);
      setCalculatedSize(size);
    } else {
      setCalculatedSize("");
    }

    setShowAddSize(true);
  };

  const handleDeleteDisplay = () => {
    setIsDeletePopupOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (editingDisplay) {
      try {
        // 서버에 삭제 상태 업데이트 (deleteState: 1)
        await updateDisplayOnServer(editingDisplay._id, { deleteState: 1 });

        // 로컬 아톰에서 삭제된 디스플레이 제거
        setDisplayList((prev) =>
          prev.filter((device) => device._id !== editingDisplay._id)
        );

        setIsDeletePopupOpen(false);
        // handleBackToSelection();
        onClose();
      } catch (error) {
        console.error("디스플레이 삭제 실패:", error);
        setIsDeletePopupOpen(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setIsDeletePopupOpen(false);
  };

  const handleEditSave = async () => {
    if (editingDisplay) {
      // 편집 모드: 서버에 업데이트
      await handleUpdateDisplay();
    } else {
      // 새 디스플레이 추가 모드 - 서버에 저장
      await handleSaveNewDisplay();
    }
    if (onClose) onClose();
  };

  // 기존 디스플레이 서버 업데이트 함수
  const handleUpdateDisplay = async () => {
    // 기존 값과 입력값 비교

    const isChanged =
      displayName !== (editingDisplay?.deviceTitle || "") ||
      selectedResolution !== (editingDisplay?.deviceSpec?.deviceDPI || "") ||
      selectedInch !== (editingDisplay?.deviceSpec?.deviceInch || "") ||
      calculatedSize.width !==
        (editingDisplay?.deviceSpec?.deviceWidthMM || "") ||
      calculatedSize.height !==
        (editingDisplay?.deviceSpec?.deviceHeightMM || "");

    if (!isChanged) {
      setShowAddSize(false);
      return;
    }

    try {
      const inchNumber = parseFloat(selectedInch); // "43인치" → 43
      const cmValue = (inchNumber * 2.54).toFixed(2) + "cm";

      let resolutionName = "";
      let resolutionDPI = "";

      if (selectedResolution.includes("|")) {
        const parts = selectedResolution.split("|");
        resolutionName = parts[0].trim();
        resolutionDPI = (parts[1] || "").trim();
      } else {
        resolutionName = selectedResolution.trim();
        resolutionDPI = selectedResolution.trim();
      }

      let resolutionWidth = "";
      let resolutionHeight = "";
      const wh = resolutionDPI.split(/\s*x\s*/i);
      resolutionWidth = (wh[0] || "").trim();
      resolutionHeight = (wh[1] || "").trim();

      const updateData = {
        deviceTitle: displayName,
        deviceSpec: {
          deviceWidth: resolutionWidth,
          deviceHeight: resolutionHeight,
          deviceWidthMM: calculatedSize.width,
          deviceHeightMM: calculatedSize.height,
          deviceCm: cmValue,
          deviceResolution: resolutionName,
          deviceDPI: selectedResolution,
          deviceInch: selectedInch,
        },
      };

      // 서버 업데이트
      await updateDisplayOnServer(editingDisplay._id, updateData);

      // 로컬 아톰 업데이트 (즉시 UI 반영)
      setDisplayList((prev) =>
        prev.map((device) =>
          device._id === editingDisplay._id
            ? { ...device, ...updateData }
            : device
        )
      );

      // 폼 초기화
      handleBackToSelection();
    } catch (error) {
      console.error("디스플레이 업데이트 실패:", error);
      // alert("디스플레이 업데이트에 실패했습니다. 다시 시도해주세요.");
    }
  };

  // 새 디스플레이 서버 저장 함수
  const handleSaveNewDisplay = async () => {
    const inchNumber = parseFloat(selectedInch); // "43인치" → 43
    const cmValue = (inchNumber * 2.54).toFixed(2) + "cm";

    let resolutionName = "";
    let resolutionDPI = "";

    if (selectedResolution.includes("|")) {
      const parts = selectedResolution.split("|");
      resolutionName = parts[0].trim();
      resolutionDPI = (parts[1] || "").trim();
    } else {
      resolutionName = selectedResolution.trim();
      resolutionDPI = selectedResolution.trim();
    }

    let resolutionWidth = "";
    let resolutionHeight = "";
    const wh = resolutionDPI.split(/\s*x\s*/i);
    resolutionWidth = (wh[0] || "").trim();
    resolutionHeight = (wh[1] || "").trim();

    const newDisplayData = {
      deviceTitle: displayName,
      deviceSpec: {
        deviceWidthMM: calculatedSize.width,
        deviceHeightMM: calculatedSize.height,
        deviceWidth: resolutionWidth,
        deviceHeight: resolutionHeight,
        deviceCm: cmValue,
        deviceResolution: resolutionName,
        deviceDPI: selectedResolution,
        deviceInch: selectedInch,
      },
    };

    try {
      // 부모 컴포넌트의 서버 저장 함수 호출 (이 함수에서 아톰도 업데이트됨)
      if (onSaveNewDisplay) {
        await onSaveNewDisplay(newDisplayData);
      }

      // console.log("새 디스플레이 서버 저장 완료:", newDisplayData);

      // 폼 초기화
      handleBackToSelection();
    } catch (error) {
      console.error("새 디스플레이 저장 실패:", error);
      // alert("디스플레이 저장에 실패했습니다. 다시 시도해주세요.");
    }
  };

  const handleResolutionToggle = () => {
    setShowResolutionDropdown(!showResolutionDropdown);
  };

  const handleResolutionSelect = (resolution) => {
    if (resolution.id === "custom") {
      setIsCustomResolution(true);
      setSelectedResolution("");
      setCustomResolutionInput("");
    } else {
      setIsCustomResolution(false);
      setSelectedResolution(resolution.label);
      setCustomResolutionInput("");
    }
    setShowResolutionDropdown(false);
  };

  const handleCustomResolutionChange = (e) => {
    const value = e.target.value;
    setCustomResolutionInput(value);
    setSelectedResolution(value);
  };

  useEffect(() => {
    if (isCustomResolution && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [isCustomResolution]);

  const handleInchToggle = () => {
    setShowInchDropdown(!showInchDropdown);
  };

  const handleInchSelect = (inch) => {
    if (inch.id === "custom") {
      setIsCustomInch(true);
      setSelectedInch("");
      setCustomInchInput("");
    } else {
      setIsCustomInch(false);
      setSelectedInch(inch.label);
      setCustomInchInput("");
    }
    setShowInchDropdown(false);
  };

  const handleCustomInchChange = (e) => {
    const value = e.target.value;
    setCustomInchInput(value);
    setSelectedInch(value);
  };

  useEffect(() => {
    if (isCustomInch && customInchInputRef.current) {
      customInchInputRef.current.focus();
    }
  }, [isCustomInch]);

  // 인치에 따른 사이즈 계산 함수
  const calculateSizeFromInch = (inch, resolution) => {
    if (!inch || !resolution) return { width: "", height: "" };

    // 해상도에서 width, height 추출
    const resMatch = resolution.match(/(\d{3,})\s*[×x*]\s*(\d{3,})/);
    if (!resMatch) return { width: "", height: "" };

    const widthPx = parseInt(resMatch[1], 10);
    const heightPx = parseInt(resMatch[2], 10);
    const inchValue = parseFloat(inch.replace(" 인치", "").replace("인치", ""));
    if (isNaN(inchValue)) return { width: "", height: "" };

    // 대각선 인치와 해상도로 실제 mm 계산
    const aspectRatio = widthPx / heightPx;
    const heightInch = inchValue / Math.sqrt(aspectRatio ** 2 + 1);
    const widthInch = heightInch * aspectRatio;

    const widthMm = Math.round(widthInch * 25.4);
    const heightMm = Math.round(heightInch * 25.4);

    return { width: widthMm.toString(), height: heightMm.toString() };
  };

  // 인치 변경 시 사이즈 자동 계산
  useEffect(() => {
    if (selectedInch) {
      const size = calculateSizeFromInch(selectedInch, selectedResolution);
      setCalculatedSize(size);
    }
  }, [selectedInch, selectedResolution]);

  useEffect(() => {
    if (selectedEditDisplayId && displayList) {
      const display = displayList.find(
        (d) => d.id === selectedEditDisplayId || d._id === selectedEditDisplayId
      );

      if (display) {
        handleEditDisplayFromDeviceSpec(display);
      }
    }
  }, [selectedEditDisplayId]);

  // 팝업이 열릴 때 바로 사이즈 추가 화면으로
  useEffect(() => {
    setShowAddSize(true);
  }, []);

  if (showAddSize) {
    return (
      <PopupContainer>
        <PopupContent>
          <Header>
            {selectedEditDisplayId === null ? (
              <Title>디스플레이를 등록해주세요</Title>
            ) : (
              <Title>디스플레이 정보 편집하기</Title>
            )}
            <CloseButton onClick={onClose}>
              <CloseButtonIcon />
            </CloseButton>
          </Header>

          <Content isAddSizeMode={true}>
            {/* 기존 디스플레이 목록 */}
            <DisplayList>
              {/* {displayOptions.map((display) => (
                <DisplayOption
                  key={display.id}
                  selected={selectedDisplay === display.id}
                >
                  <RadioButton onClick={() => handleDisplaySelect(display.id)}>
                    <RadioButtonInner
                      selected={selectedDisplay === display.id}
                    />
                  </RadioButton>
                  <DisplayInfo>
                    <DisplayTitle>{display.title}</DisplayTitle>
                    <DisplaySpecs>{display.specs}</DisplaySpecs>
                  </DisplayInfo>
                  <EditIconButton onClick={() => handleEditDisplay(display)}>
                    <EditIcon />
                  </EditIconButton>
                </DisplayOption>
              ))}
             */}

              {/* 새 디스플레이 추가 섹션 */}
              <AddDisplaySection>
                <InputGroup>
                  <Label>디스플레이명</Label>
                  <InputBox>
                    <InputText
                      placeholder="디스플레이 명을 입력해주세요(15자 이내)"
                      value={displayName}
                      maxLength={15}
                      onChange={(e) =>
                        setDisplayName(e.target.value.slice(0, 15))
                      }
                    />
                  </InputBox>
                </InputGroup>

                <InputRow>
                  <InputGroup>
                    <Label>해상도</Label>
                    <ResolutionSelectContainer>
                      {isCustomResolution ? (
                        <CustomResolutionInput
                          ref={customInputRef}
                          placeholder="해상도를 입력해주세요 (예: 1920 x 1080)"
                          value={customResolutionInput}
                          onChange={handleCustomResolutionChange}
                        />
                      ) : (
                        <SelectBox onClick={handleResolutionToggle}>
                          <SelectText>
                            {selectedResolution || "선택하세요"}
                          </SelectText>
                          <ArrowIcon isOpen={showResolutionDropdown} />
                        </SelectBox>
                      )}
                      {showResolutionDropdown && (
                        <ResolutionDropdown
                          style={{ maxHeight: "310px", overflowY: "auto" }}
                        >
                          {resolutionOptions.map((resolution) => (
                            <ResolutionOption
                              key={resolution.id}
                              onClick={() => handleResolutionSelect(resolution)}
                            >
                              <ResolutionCheckbox />
                              <ResolutionLabel>
                                {resolution.label}
                              </ResolutionLabel>
                            </ResolutionOption>
                          ))}
                        </ResolutionDropdown>
                      )}
                    </ResolutionSelectContainer>
                  </InputGroup>

                  <InputGroup>
                    <Label>인치</Label>
                    <InchSelectContainer>
                      {isCustomInch ? (
                        <CustomInchInput
                          ref={customInchInputRef}
                          placeholder="인치를 입력해주세요 (예: 24)"
                          value={customInchInput}
                          onChange={handleCustomInchChange}
                        />
                      ) : (
                        <SelectBox onClick={handleInchToggle}>
                          <SelectText>
                            {selectedInch || "선택하세요"}
                          </SelectText>
                          <ArrowIcon isOpen={showInchDropdown} />
                        </SelectBox>
                      )}
                      {showInchDropdown && (
                        <InchDropdown
                          style={{ maxHeight: "310px", overflowY: "auto" }}
                        >
                          {inchOptions.map((inch) => (
                            <InchOption
                              key={inch.id}
                              onClick={() => handleInchSelect(inch)}
                            >
                              <InchCheckbox />
                              <InchLabel>{inch.label}</InchLabel>
                            </InchOption>
                          ))}
                        </InchDropdown>
                      )}
                    </InchSelectContainer>
                  </InputGroup>
                </InputRow>

                <InputGroup>
                  <div
                    style={{
                      display: "flex",
                      gap: "20px",
                      marginBottom: "4px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <Label style={{ display: "block", textAlign: "left" }}>
                        화면사이즈 (대각선 길이)
                      </Label>
                    </div>
                    <div style={{ flex: 2, display: "flex", gap: "20px" }}>
                      <Label
                        style={{ display: "block", textAlign: "left", flex: 1 }}
                      >
                        화면 가로/세로 길이
                      </Label>
                      <div style={{ flex: 1 }}></div>
                    </div>
                  </div>
                  <SizeInputRow>
                    <SizeBox>
                      <SizeText>
                        {calculatedSize.width && calculatedSize.height
                          ? Math.round(
                              Math.sqrt(
                                Math.pow(Number(calculatedSize.width), 2) +
                                  Math.pow(Number(calculatedSize.height), 2)
                              )
                            )
                          : "대각선 길이"}
                      </SizeText>
                      <Unit>mm</Unit>
                    </SizeBox>
                    <SizeBox>
                      <SizeText>{calculatedSize.width || "가로 길이"}</SizeText>
                      <Unit>mm</Unit>
                    </SizeBox>
                    <SizeBox>
                      <SizeText>
                        {calculatedSize.height || "세로 길이"}
                      </SizeText>
                      <Unit>mm</Unit>
                    </SizeBox>
                  </SizeInputRow>
                </InputGroup>

                <ButtonRow hasDelete={!!editingDisplay}>
                  {editingDisplay && (
                    <DeleteButton onClick={handleDeleteDisplay}>
                      <TrashIcon />
                      디스플레이 삭제하기
                    </DeleteButton>
                  )}
                  <RightButtonGroup>
                    <CancelButton onClick={handleBackToSelection}>
                      {editingDisplay ? "편집 취소" : "취소"}
                    </CancelButton>
                    <SaveButton
                      disabled={
                        !displayName || !selectedResolution || !selectedInch
                      }
                      onClick={handleEditSave}
                    >
                      {editingDisplay ? "편집 저장" : "새 디스플레이 저장"}
                    </SaveButton>
                  </RightButtonGroup>
                </ButtonRow>
              </AddDisplaySection>
            </DisplayList>
          </Content>
        </PopupContent>

        {isDeletePopupOpen && (
          <PopupWrap
            Warning={true}
            title="디스플레이를 삭제하시겠습니까?"
            message="삭제된 디스플레이는 복구할 수 없습니다."
            buttonType="Outline"
            closeText="취소"
            confirmText="삭제"
            isModal={false}
            onCancel={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
          />
        )}
      </PopupContainer>
    );
  }

  return (
    <>
      <PopupContainer>
        <PopupContent>
          <Header>
            {displayOptions.length === 0 ? (
              <Title>디스플레이를 등록해주세요</Title>
            ) : (
              <Title>디스플레이를 선택해주세요</Title>
            )}
            <CloseButton onClick={onClose}>
              <CloseButtonIcon />
            </CloseButton>
          </Header>

          <Content>
            <DisplayList>
              {/* {displayOptions.length === 0 && (
                <BoxWrap NoData style={{ height: "200px" }}>
                  <img src={images.ListFillPrimary} alt="" />
                  <Body2 color="gray700" align="center !important">
                    아직 등록된 디스플레이가 없어요. <br />
                    디스플레이를 등록하고 화면을 구성해보세요.
                  </Body2>
                  <Button
                    Medium
                    Outline
                    onClick={handleAddSizeClick}
                  >
                    <Caption1 color="gray700">디스플레이 등록하기</Caption1>
                  </Button>
                </BoxWrap>
              )} */}
              {/* {displayOptions.map((display) => (
                <DisplayOption
                  key={display.id}
                  selected={selectedDisplay === display.id}
                >
                  <RadioButton onClick={() => handleDisplaySelect(display.id)}>
                    <RadioButtonInner
                      selected={selectedDisplay === display.id}
                    />
                  </RadioButton>
                  <DisplayInfo>
                    <DisplayTitle>{display.title}</DisplayTitle>
                    <DisplaySpecs>{display.specs}</DisplaySpecs>
                  </DisplayInfo>
                  <EditIconButton onClick={() => handleEditDisplay(display)}>
                    <EditIcon />
                  </EditIconButton>
                </DisplayOption>
              ))}

              {displayOptions.length !== 0 && (
                  <AddSizeButton onClick={handleAddSizeClick}>
                  <PlusIcon />
                  사이즈 추가하기
              </AddSizeButton>
                
                )}
                           */}
            </DisplayList>
          </Content>
          {/* 
          <Footer>
            <CompleteButton
              onClick={handleComplete}
              disabled={!selectedDisplay}
            >
              선택 완료
            </CompleteButton>
          </Footer> */}
        </PopupContent>
      </PopupContainer>

      {/* 삭제 확인 팝업 */}
      {isDeletePopupOpen && (
        <PopupWrap
          Warning={true}
          title="디스플레이를 삭제하시겠습니까?"
          message="삭제된 디스플레이는 복구할 수 없습니다."
          buttonType="Outline"
          closeText="취소"
          confirmText="삭제"
          isModal={false}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
};

export default AutodisplayApiSelectedPopup;

// Styled Components
const PopupContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const PopupContent = styled.div`
  width: 880px;
  height: 641px;
  background: white;
  border-radius: 10px;
  border: 1px solid #e0e4eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 40px 40px 0 40px;
  flex-shrink: 0;
`;

const Title = styled.h2`
  color: #131416;
  font-size: 24px;
  font-family: Pretendard;
  font-weight: 700;
  line-height: 36px;
  margin: 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: ${(props) =>
    props.isAddSizeMode ? "48px 40px 32px 40px" : "48px 40px 0 40px"};
  overflow-y: auto;
  flex: 1;
`;

const Footer = styled.div`
  padding: 32px 40px 40px 40px;
  flex-shrink: 0;
  border-top: 1px solid rgba(179, 183, 186, 0.2);
`;

const DisplayList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DisplayOption = styled.div`
  width: 100%;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid
    ${(props) => (props.selected ? "#256EF4" : "rgba(179, 183, 186, 0.50)")};
  background: ${(props) =>
    props.selected ? "rgba(37, 110, 244, 0.05)" : "white"};
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;
`;

const RadioButton = styled.div`
  width: 24px;
  height: 24px;
  padding: 2px;
  background: white;
  border-radius: 50%;
  border: 1px solid #58616a;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:hover {
    border-color: #256ef4;
  }
`;

const RadioButtonInner = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(props) => (props.selected ? "#256EF4" : "transparent")};
`;

const DisplayInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const DisplayTitle = styled.div`
  color: #131416;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 600;
  line-height: 28.5px;
`;

const DisplaySpecs = styled.div`
  color: #090f15;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
`;

const EditIcon = () => (
  <svg
    width="32"
    height="33"
    viewBox="0 0 32 33"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M21.9124 4.12883C22.329 3.71227 23.0044 3.71227 23.4209 4.12883L28.7542 9.46216C28.9543 9.6622 29.0667 9.93351 29.0667 10.2164C29.0667 10.4993 28.9543 10.7706 28.7542 10.9707L16.7542 22.9707C16.5542 23.1707 16.2829 23.2831 16 23.2831H10.6667C10.0776 23.2831 9.6 22.8055 9.6 22.2164V16.8831C9.6 16.6002 9.71238 16.3289 9.91242 16.1288L21.9124 4.12883ZM22.6667 6.39157L20.1752 8.88307L24 12.7079L26.4915 10.2164L22.6667 6.39157ZM22.4915 14.2164L18.6667 10.3916L11.7333 17.3249V21.1497H15.5582L22.4915 14.2164ZM6.4 7.01641C6.25272 7.01641 6.13333 7.1358 6.13333 7.28307V26.4831C6.13333 26.6303 6.25272 26.7497 6.4 26.7497H25.6C25.7473 26.7497 25.8667 26.6303 25.8667 26.4831V17.1497C25.8667 16.5606 26.3442 16.0831 26.9333 16.0831C27.5224 16.0831 28 16.5606 28 17.1497V26.4831C28 27.8086 26.9255 28.8831 25.6 28.8831H6.4C5.07452 28.8831 4 27.8086 4 26.4831V7.28307C4 5.95759 5.07452 4.88307 6.4 4.88307H15.7323C16.3214 4.88307 16.799 5.36064 16.799 5.94974C16.799 6.53884 16.3214 7.01641 15.7323 7.01641H6.4Z"
      fill="#8A949E"
    />
  </svg>
);

const AddSizeButton = styled.button`
  width: 100%;
  height: 56px;
  padding: 0 16px;
  background: rgba(179, 183, 186, 0.1);
  border-radius: 8px;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #8a949e;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28.5px;

  &:hover {
    background: rgba(179, 183, 186, 0.15);
  }
`;

const PlusIcon = () => (
  <svg
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.99902 2.88281C10.3672 2.88281 10.666 3.18161 10.666 3.5498V9.7168H16.833C17.2011 9.7168 17.4999 10.0147 17.5 10.3828C17.5 10.751 17.2012 11.0498 16.833 11.0498H10.666V17.2158C10.666 17.584 10.3672 17.8828 9.99902 17.8828C9.63098 17.8826 9.33301 17.5839 9.33301 17.2158V11.0498H3.16699C2.7988 11.0498 2.5 10.751 2.5 10.3828C2.50009 10.0147 2.79886 9.7168 3.16699 9.7168H9.33301V3.5498C9.33301 3.18172 9.63098 2.88299 9.99902 2.88281Z"
      fill="#8A949E"
    />
  </svg>
);

const CompleteButton = styled.button`
  width: 100%;
  height: 48px;
  min-width: 78px;
  padding: 0 16px;
  background: ${(props) => (props.disabled ? "#ccc" : "#256EF4")};
  border-radius: 6px;
  border: none;
  color: white;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: #1e5ad1;
  }
`;

// Add Size Form Styles
const AddDisplaySection = styled.div`
  padding: 20px;
  border-radius: 10px;
  border: 1px solid rgba(179, 183, 186, 0.5);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const Label = styled.label`
  color: #464c53;
  font-size: 15px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 22.5px;
  text-align: left;
`;

const InputBox = styled.div`
  height: 56px;
  padding: 0 16px;
  background: rgba(179, 183, 186, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
`;

const InputText = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  color: #323232;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28.5px;
  outline: none;

  &::placeholder {
    color: #8a949e;
  }
`;

const SelectBox = styled.div`
  width: 100%;
  height: 56px;
  padding: 0 16px;
  background: rgba(179, 183, 186, 0.1);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const SelectText = styled.span`
  color: #323232;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28.5px;
`;

const ArrowIcon = ({ isOpen }) => (
  <svg
    width="24"
    height={isOpen ? "24" : "25"}
    viewBox={isOpen ? "0 0 24 24" : "0 0 24 25"}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {isOpen ? (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M19.7344 15.5954C20.0628 15.2999 20.0894 14.7941 19.7939 14.4656L12.5947 6.46563C12.443 6.29705 12.2268 6.20078 12 6.20077C11.7732 6.20077 11.5571 6.29703 11.4054 6.4656L4.20537 14.4656C3.9098 14.794 3.93643 15.2998 4.26484 15.5954C4.59324 15.891 5.09908 15.8644 5.39464 15.5359L12 8.19669L18.6046 15.5359C18.9001 15.8643 19.4059 15.891 19.7344 15.5954Z"
        fill="#33363D"
      />
    ) : (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M19.7344 8.78737C20.0628 9.08292 20.0894 9.58875 19.7939 9.91718L12.5947 17.9172C12.443 18.0858 12.2268 18.182 12 18.182C11.7732 18.182 11.5571 18.0858 11.4054 17.9172L4.20537 9.91721C3.9098 9.5888 3.93643 9.08297 4.26484 8.7874C4.59324 8.49184 5.09908 8.51846 5.39464 8.84687L12 16.1861L18.6046 8.8469C18.9001 8.51847 19.4059 8.49182 19.7344 8.78737Z"
        fill="#33363D"
      />
    )}
  </svg>
);

const InputRow = styled.div`
  display: flex;
  gap: 20px;
`;

const SizeInputRow = styled.div`
  display: flex;
  gap: 20px;
`;

const SizeBox = styled.div`
  flex: 1;
  height: 56px;
  padding: 0 16px;
  background: rgba(179, 183, 186, 0.1);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  // &:first-child {
  //   flex: 2;
  //   min-width: 180px;
  // }
  // &:not(:first-child) {
  //   min-width: 120px;
  // }
`;

const SizeText = styled.span`
  color: #8a949e;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28.5px;
`;

const Unit = styled.span`
  color: #8a949e;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28.5px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: ${(props) =>
    props.hasDelete ? "space-between" : "flex-end"};
  align-items: center;
  gap: 20px;
`;

const RightButtonGroup = styled.div`
  display: flex;
  gap: 20px;
`;

const EditIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const DeleteButton = styled.button`
  height: 48px;
  padding: 0 16px;
  background: rgba(255, 0, 0, 0.1);
  border-radius: 6px;
  border: none;
  color: #ff0000;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;

  &:hover {
    background: rgba(255, 0, 0, 0.15);
  }
`;

const TrashIcon = () => (
  <div
    style={{
      width: "24px",
      height: "24px",
      position: "relative",
      opacity: 0.1,
    }}
  >
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 6H5H21"
        stroke="#FF0000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
        stroke="#FF0000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 11V17"
        stroke="#FF0000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 11V17"
        stroke="#FF0000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const CancelButton = styled.button`
  width: 120px;
  height: 48px;
  min-width: 78px;
  padding: 0 16px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.3);
  background: white;
  color: #131416;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
`;

const SaveButton = styled.button`
  width: 200px;
  height: 48px;
  min-width: 78px;
  padding: 0 16px;
  background: black;
  border-radius: 6px;
  border: none;
  color: white;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
  cursor: pointer;

  &:hover {
    background: #333;
  }

  &:disabled {
    background: #f5f5f5;
    color: #666666;
    cursor: not-allowed;
  }
`;

// CloseButtonIcon SVG 컴포넌트
const CloseButtonIcon = () => (
  <svg
    width="16"
    height="22"
    viewBox="0 0 16 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.6767 2.47245C16.1079 2.02132 16.1079 1.28988 15.6767 0.838745C15.2455 0.387609 14.5464 0.387609 14.1152 0.838745L7.99942 7.23754L1.8846 0.839765C1.45341 0.388629 0.754327 0.388629 0.323143 0.839765C-0.10804 1.2909 -0.10804 2.02234 0.323144 2.47347L6.43797 8.87124L1.03279 14.5265C0.601602 14.9777 0.601602 15.7091 1.03279 16.1602C1.46397 16.6114 2.16306 16.6114 2.59424 16.1602L7.99943 10.505L13.4056 16.1613C13.8368 16.6124 14.5359 16.6124 14.967 16.1613C15.3982 15.7101 15.3982 14.9787 14.967 14.5276L9.56088 8.87124L15.6767 2.47245Z"
      fill="#666666"
    />
  </svg>
);

// CloseButton 스타일
const CloseButton = styled.div`
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

// Resolution Dropdown Styles
const ResolutionSelectContainer = styled.div`
  position: relative;
`;

const ResolutionDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  border: 1px solid #cdd1d5;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);
  z-index: 10;
  padding: 8px;
  margin-top: 4px;
`;

const ResolutionOption = styled.div`
  height: 48px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background: rgba(179, 183, 186, 0.1);
  }
`;

const ResolutionCheckbox = styled.div`
  width: 16px;
  height: 16px;
  padding: 2px;
  background: white;
  border-radius: 4px;
  border: 1px solid #bebebe;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const ResolutionLabel = styled.span`
  color: #1e2124;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
`;

const CustomResolutionInput = styled.input`
  width: 100%;
  height: 56px;
  padding: 0 16px;
  background: rgba(179, 183, 186, 0.1);
  border-radius: 8px;
  border: none;
  color: #1e2124;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28.5px;
  outline: none;

  &::placeholder {
    color: #8a949e;
  }

  &:focus {
    background: rgba(179, 183, 186, 0.1);
  }
`;

// Inch Dropdown Styles
const InchSelectContainer = styled.div`
  position: relative;
`;

const InchDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  border: 1px solid #cdd1d5;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);
  z-index: 10;
  padding: 8px;
  margin-top: 4px;
`;

const InchOption = styled.div`
  height: 48px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background: rgba(179, 183, 186, 0.1);
  }
`;

const InchCheckbox = styled.div`
  width: 16px;
  height: 16px;
  padding: 2px;
  background: white;
  border-radius: 4px;
  border: 1px solid #bebebe;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const InchLabel = styled.span`
  color: #1e2124;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
`;

const CustomInchInput = styled.input`
  width: 100%;
  height: 56px;
  padding: 0 16px;
  background: rgba(179, 183, 186, 0.1);
  border-radius: 8px;
  border: none;
  color: #1e2124;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28.5px;
  outline: none;

  &::placeholder {
    color: #8a949e;
  }

  &:focus {
    background: rgba(179, 183, 186, 0.1);
  }
`;
